import React from 'react';
import { cn } from '@/lib/utils';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { TIMELINE_ANNOTATION_BLOCK_CLASS } from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';

export interface ScenarioOption<K extends string = string> {
  key: K;
  label: string;
  symbol: string;
  description: string;
}

interface ScenarioTogglePanelProps<K extends string> {
  options: ScenarioOption<K>[];
  scenario: K;
  onScenarioChange: (key: K) => void;
  children: React.ReactNode;
  className?: string;
  /** Accessible name for the underlying radiogroup (falls back to "Scenario"). */
  ariaLabel?: string;
  'data-anim-audit'?: string;
  'data-anim-audit-scenario'?: string;
}

/** Card wrapper with a SegmentedControl scenario toggle and an active-scenario description. */
export function ScenarioTogglePanel<K extends string>({
  options,
  scenario,
  onScenarioChange,
  children,
  className,
  ariaLabel = 'Scenario',
  ...rest
}: ScenarioTogglePanelProps<K>) {
  const activeDescription = options.find((o) => o.key === scenario)?.description;

  return (
    <div
      className={cn('rounded border border-border bg-surface-1/50 p-3 mb-3 flex flex-col gap-2', className)}
      {...rest}
    >
      <SegmentedControl
        size="compact"
        options={options.map(({ key, label, symbol }) => ({
          value: key,
          label: <><span className="mr-1">{symbol}</span>{label}</>,
        }))}
        value={scenario}
        onChange={(v) => onScenarioChange(v as K)}
        ariaLabel={ariaLabel}
      />

      <p className={cn(TIMELINE_ANNOTATION_BLOCK_CLASS, 'mb-1')}>
        {activeDescription}
      </p>

      {children}
    </div>
  );
}
